import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseLanguageTTS {
  speak: (text: string, lang: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
  cancel: () => void;
}

export function useLanguageTTS(): UseLanguageTTS {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [identityVersion, setIdentityVersion] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mounted = useRef(false), generation = useRef(0), authEpoch = useRef(0);
  const owner = useRef<string | null | undefined>(undefined);
  const active = useRef<AbortController | null>(null);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.src = "";
    }
  }, []);

  const cancel = useCallback(() => {
    generation.current++;
    active.current?.abort();
    active.current = null;
    stopAudio();
    if (mounted.current) setIsLoading(false);
  }, [stopAudio]);

  useEffect(() => {
    mounted.current = true;
    setIdentityVersion(authEpoch.current);
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted.current) return;
      const next = session?.user.id ?? null;
      if (owner.current === next) return;
      owner.current = next;
      authEpoch.current++;
      cancel();
      setError(null);
      setIdentityVersion(authEpoch.current);
    });
    const lifetime = { mounted, authEpoch };
    return () => {
      lifetime.mounted.current = false;
      lifetime.authEpoch.current++;
      cancel();
      subscription.unsubscribe();
    };
  }, [cancel]);

  const speak = useCallback(async (text: string, lang: string) => {
    // A callback retained by a retired identity must not submit its old text.
    if (!mounted.current || identityVersion !== authEpoch.current) return;
    const ticket = ++generation.current, epoch = authEpoch.current;
    active.current?.abort();
    stopAudio();
    const controller = new AbortController();
    active.current = controller;
    const current = () => mounted.current && ticket === generation.current
      && epoch === authEpoch.current && !controller.signal.aborted;
    setError(null);
    setIsLoading(true);

    try {
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (!current()) return;
      if (authError || !session?.user.id || !session.access_token) {
        throw authError ?? new Error("Not authenticated");
      }
      const expectedOwner = session.user.id;
      if (owner.current === undefined) owner.current = expectedOwner;
      if (owner.current !== expectedOwner) throw new Error("Language TTS account changed");
      const { data, error: fnError } = await supabase.functions.invoke("language-tts", {
        body: { text, lang },
        headers: { Authorization: "Bearer " + session.access_token },
        signal: controller.signal,
      });
      if (!current() || owner.current !== expectedOwner) return;
      if (fnError) throw fnError;
      if (typeof data?.url !== "string" || !data.url) throw new Error("No audio URL returned");

      const audio = new Audio(data.url);
      audioRef.current = audio;
      audio.onended = () => {
        if (!current() || audioRef.current !== audio) return;
        audioRef.current = null;
        setIsLoading(false);
      };
      audio.onerror = () => {
        if (!current() || audioRef.current !== audio) return;
        stopAudio();
        setError(new Error("Audio playback failed"));
        setIsLoading(false);
      };

      await audio.play();
      if (!current()) audio.pause();
    } catch (e) {
      if (!current()) return;
      stopAudio();
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      setIsLoading(false);
      throw err;
    } finally {
      if (active.current === controller) active.current = null;
    }
  }, [identityVersion, stopAudio]);

  return { speak, isLoading, error, cancel };
}
